// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: c0b90e24-901e-497e-b499-3a0b3aae3c51

// ---------------------------------------------------------------------------
// CLOUD DISCOVERY — what a client RUNS
//
// The companion to FinOps, which answers what a client SPENDS. Those were the
// same screen for a while and it hid a real gap: the built-versus-specified
// audit derived a running instance count from Cost Explorer USAGE HOURS, an
// average over a window that reports "1.43 instances" for an account running
// four. Fine as a cost signal, useless as an asset list — you cannot open a
// fact sheet on 0.84 of a server.
//
// Read-only in both directions. The backend calls Describe/List against the
// client's account and writes nothing to AWS; this screen writes nothing to
// Primordia. Importing discovered assets as fact sheets is deliberately a
// separate step that does not exist yet: an estate already loaded from RVTools
// under different names would gain a duplicate of every server, and undoing
// that across 7,000 rows is far more expensive than not doing it.
// ---------------------------------------------------------------------------
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import { getConfig } from './config';
import { getIdToken } from './auth';
import { exportCsv } from './exports';
import HelpBubble from './HelpBubble';

const HEAD = 'sectionHeader.main';

// Running is unremarkable; anything else is the interesting case (a stopped
// instance is paid-for storage doing nothing, or a migration leftover), so only
// the exceptions are coloured rather than painting the whole column.
const stateColor = (s) => {
  if (s === 'running' || s === 'available') return 'success';
  if (s === 'stopped' || s === 'stopping') return 'warning';
  if (s === 'terminated' || s === 'shutting-down') return 'error';
  return 'default';
};

export default function CloudDiscovery({ tenantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  // Azure is fetched alongside AWS and MERGED, because "what does this client
  // run" is one question. The asset shape was normalised to the same vocabulary
  // in both providers precisely so one table could render either.
  const [azure, setAzure] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}`, ...(options.headers || {}) },
    });
    const text = await res.text();
    // An error envelope is frequently HTML rather than JSON (a gateway refusal,
    // a WAF block), and a bare JSON.parse throws before the real status can be
    // reported — the same guard every other screen's api() carries.
    let body = {};
    let parseFailed = false;
    try { body = text ? JSON.parse(text) : {}; } catch (e) { parseFailed = true; }
    if (!res.ok) throw new Error(body.error || body.message || `request failed (${res.status})`);
    if (parseFailed) throw new Error(`The API returned ${res.status} with a body that is not JSON.`);
    return body;
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      // Discovery is per client BY DEFINITION — it reads one client's cloud
      // account. The master workspace has no account of its own to crawl, so it
      // must name a client rather than silently crawling something.
      if (!tenantId || tenantId === 'optimum') {
        setData(null);
        setErr('Choose a client workspace — cloud discovery reads one client’s account, '
          + 'and the master workspace has none of its own.');
        return;
      }
      setData(await api(`/inventory/discover?tenantId=${encodeURIComponent(tenantId)}`));
      try {
        const az = await api(`/finops/azure/insights?tenantId=${encodeURIComponent(tenantId)}&want=inventory`);
        setAzure(az && az.connected ? az : null);
      } catch (e) { setAzure(null); }   // one cloud must not blank the other
    } catch (e) {
      setErr(e.message); setData(null);
    } finally { setLoading(false); }
  }, [api, tenantId]);

  useEffect(() => { load(); }, [load]);

  const awsAssets = ((data && data.assets) || []).map((a) => ({ ...a, provider: a.provider || 'aws' }));
  const azureAssets = ((azure && azure.inventory && azure.inventory.assets) || [])
    .map((a) => ({ ...a, provider: 'azure' }));
  const assets = [...awsAssets, ...azureAssets];

  const runImport = async () => {
    if (!picked.size) return;
    setImportBusy(true); setImportResult(null);
    try {
      const chosen = assets.filter((a) => picked.has(`${a.provider}:${a.id}`));
      const out = await api('/inventory/import', {
        method: 'POST',
        body: JSON.stringify({ tenantId, assets: chosen }),
      });
      setImportResult(out); setPicked(new Set());
    } catch (e) { setErr(e.message); } finally { setImportBusy(false); }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD }}>Cloud discovery</Typography>
        <HelpBubble topic="cloudDiscovery" />
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Re-scan
        </Button>
        <Button
          size="small" startIcon={<DownloadIcon />} disabled={!assets.length}
          onClick={() => exportCsv('cloud-discovery', assets.map((a) => ({
            Kind: a.kind, Name: a.name, Id: a.id, Type: a.instanceType, State: a.state,
            Region: a.region, AZ: a.availabilityZone || '', Platform: a.platform || a.engine || '',
            PrivateIP: a.privateIp || '', VPC: a.vpcId || '',
          })))}
        >
          Export CSV
        </Button>
      </Stack>

      {picked.size > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}
          action={<Button size="small" variant="contained" disabled={importBusy} onClick={runImport}>
            Add {picked.size} to server inventory</Button>}>
          {picked.size} discovered asset{picked.size === 1 ? '' : 's'} selected. Importing creates a
          server record you can then attach an application to.
        </Alert>
      )}
      {importResult && (
        <Alert severity={importResult.created ? 'success' : 'info'} sx={{ mb: 2 }}>
          Created {importResult.created}, skipped {importResult.skipped}.
          {!!importResult.skipped && (
            <ul style={{ margin: '4px 0 0 16px' }}>
              {(importResult.rows || []).filter((r) => r.status === 'skipped').slice(0, 6)
                .map((r, i) => <li key={r.factSheetId || i}>{r.name} &mdash; {r.reason}</li>)}
            </ul>
          )}
        </Alert>
      )}
      {loading && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><span>Scanning…</span></Stack>}
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {!loading && data && !data.connected && (
        <Alert severity="info" icon={<CloudOffIcon />}>
          No cloud account is connected for this workspace{data.detail ? ` — ${data.detail}` : ''}.
          Connect one on the FinOps screen; discovery uses the same connection.
        </Alert>
      )}

      {!loading && data && data.connected && (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
            {data.accountId && <Chip size="small" label={`Account ${data.accountId}`} />}
            <Chip size="small" label={`${data.counts.total} asset${data.counts.total === 1 ? '' : 's'}`} color="primary" />
            {Object.entries(data.counts.byKind).map(([k, n]) => <Chip key={k} size="small" label={`${k.toUpperCase()} ${n}`} />)}
            {Object.entries(data.counts.byState).map(([k, n]) => (
              <Chip key={k} size="small" color={stateColor(k)} variant="outlined" label={`${k} ${n}`} />
            ))}
            <Chip size="small" variant="outlined" label={`regions: ${(data.regionsScanned || []).join(', ') || '—'}`} />
          </Stack>

          {/* A partial crawl that looks complete is the failure this whole
              feature exists to avoid, so the caveat sits above the table rather
              than in a console nobody reads. */}
          {data.partial && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This scan is incomplete — some regions or services refused to answer, so the
              counts above are a floor, not a total.
              <ul style={{ margin: '4px 0 0 16px' }}>
                {(data.errors || []).map((e, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={i}>{[e.region, e.service].filter(Boolean).join(' / ')}: {e.detail}</li>
                ))}
              </ul>
            </Alert>
          )}

          {assets.length === 0 ? (
            <Alert severity="info">
              The account is reachable but no EC2 or RDS assets were found in the scanned regions.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={picked.size > 0 && picked.size < assets.length}
                        checked={assets.length > 0 && picked.size === assets.length}
                        onChange={(e) => setPicked(e.target.checked
                          ? new Set(assets.map((a) => `${a.provider}:${a.id}`)) : new Set())} />
                    </TableCell>
                    {['Name', 'Cloud', 'Kind', 'Type', 'State', 'Region', 'Platform / engine', 'Identifier']
                      .map((h) => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assets.map((a) => (
                    <TableRow key={`${a.provider}:${a.id}`} hover selected={picked.has(`${a.provider}:${a.id}`)}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={picked.has(`${a.provider}:${a.id}`)} onChange={(e) => {
                          const k = `${a.provider}:${a.id}`; const next = new Set(picked);
                          if (e.target.checked) next.add(k); else next.delete(k);
                          setPicked(next);
                        }} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{a.name}</TableCell>
                      <TableCell>{a.provider === 'azure' ? 'Azure' : 'AWS'}</TableCell>
                      <TableCell>{String(a.kind).toUpperCase()}</TableCell>
                      <TableCell>{a.instanceType || '—'}</TableCell>
                      <TableCell><Chip size="small" color={stateColor(a.state)} label={a.state} /></TableCell>
                      <TableCell>{a.availabilityZone || a.region}</TableCell>
                      <TableCell>{a.engine ? `${a.engine} ${a.engineVersion || ''}`.trim() : (a.platform || '—')}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{a.id}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
}
